// src/components/CarCard.tsx

import { deleteCar } from "@/services/apiCars"
import { convertDriveUrl, daysSince, formatKilo, formatPrice } from "@/utils/helpers"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  ArrowUpRight,
  Bookmark,
  Calendar,
  Fuel,
  Gauge,
  Settings,
} from "lucide-react"
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"
import toast from "react-hot-toast"
import { BsArchive, BsTrash } from "react-icons/bs"
import { CgProfile } from "react-icons/cg"
import { FaRegEdit, FaRegEye, FaWhatsapp } from "react-icons/fa"
import { FaEllipsisVertical } from "react-icons/fa6"
import { IoMdCopy } from "react-icons/io"
import { MdOutlinePublish } from "react-icons/md"
import { TfiEmail } from "react-icons/tfi"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Cliente } from "../../api/models/clientes"
import { CarFormData } from "../types/carFormData"
import { ImageComponent } from "./ImageComponent"
import Modal from "./Modal"

export const CarCard = (props: { car: CarFormData & Cliente }) => {
  return (
    <Card className="max-w-sm rounded-2xl shadow-sm overflow-hidden hover:shadow-xl transition">
      <div className="relative my-0 mx-auto">
        <ImageComponent
          src={`${props.car.portadaUrl ? convertDriveUrl(props.car.portadaUrl) : ""}`}
          alt={props.car.marca + " " + props.car.modelo}
          className="w-full h-48 object-cover"
        />
        <span className="absolute top-2 left-2 bg-green-600 text-white text-xs font-medium px-3 py-1 rounded-full">
          Disponible
        </span>
        {/* <button className="absolute top-2 right-2 bg-white p-2 rounded-full shadow">
          <Bookmark size={16} />
        </button> */}
      </div>
      <CardContent className="md:p-6">
        <h3 className="text-xl font-bold mb-2">
          {props.car.marca + " " + props.car.modelo}
        </h3>
        <div className="grid grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
          <div className="flex items-center space-x-1">
            <Gauge size={16} />
            <span>{formatKilo(props.car.kilometros)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Fuel size={16} />
            <span>{props.car.combustible}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Settings size={16} />
            <span>{props.car.transmision}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Calendar size={16} />
            <span>{props.car.año}</span>
          </div>
        </div>
        <div className="flex items-center mt-5">
          <div className="">
            {props.car.avaluo && (
              <span className="line-through text-gray-400 text-sm">
                {formatPrice(props.car.avaluo)}
              </span>
            )}

            <p className="text-2xl font-semibold text-gray-900 mb-4">
              {formatPrice(props.car.precio)}
            </p>
          </div>
          <Link
            to={`/carros/${props.car._id}`}
            className="flex justify-end items-center gap-2 w-full text-gray-900 font-semibold text-[0.9rem] md:text-[1rem]  hover:underline cursor-pointer"
          >
            Ver Detalles
            <ArrowUpRight size={17} />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

export const CardWithMenu = (props: {
  car: CarFormData
  setCar: Dispatch<SetStateAction<CarFormData | null>>
  onOpenModal: () => void
}) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navitage = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const handleCopyClick = (e: any) => {
    try {
      window.navigator.clipboard.writeText(e.target.innerText)
      alert("Copied to clipboard!")
    } catch (err) {
      console.error("Unable to copy to clipboard.", err)
      alert("Copy to clipboard failed.")
    }
  }

  const onOpen = () => {
    setOpenModal(true)
  }

  // Mutation for deleting a car
  const { mutate: deleteCarMutation } = useMutation({
    mutationFn: ({ _id, folderId }: { _id: string; folderId: string }) =>
      deleteCar(_id, folderId),
    onSuccess: () => {
      toast.success("El carro se eliminó correctamente", { duration: 5000 })
      queryClient.invalidateQueries({ queryKey: ["cars", parseInt(id!!), 6] })
    },
    onError: (error) => {
      toast.error(`Algo salió mal eliminando el carro: ${error}`, {
        duration: 5000,
      })
    },
  })

  if (!id) {
    throw new Error("URL no tiene el parametro id")
  }

  const toggleMenu = () => {
    setIsMenuVisible(!isMenuVisible)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsMenuVisible(false)
    }
  }

  useEffect(() => {
    if (isMenuVisible) {
      document.addEventListener("mousedown", handleClickOutside)
    } else {
      document.removeEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isMenuVisible])

  return (
    <div className="relative my-0 mx-auto">
      <Card className="max-w-sm rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition">
        <div className="relative">
          <ImageComponent
            src={`${props.car.portadaUrl ? convertDriveUrl(props.car.portadaUrl) : ""}`}
            alt={props.car.marca + " " + props.car.modelo}
            className="w-full h-48 object-cover"
          />
          <span className="absolute top-2 left-2 bg-green-600 text-white text-xs font-medium px-3 py-1 rounded-full">
            Disponible
          </span>
          <button className="absolute top-2 right-2 bg-white p-2 rounded-full shadow">
            <Bookmark size={16} />
          </button>
        </div>
        <CardContent className="md:p-6">
          <h3 className="text-xl font-bold mb-2">
            {props.car.marca + " " + props.car.modelo}
          </h3>
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Gauge size={16} />
              <span>{formatKilo(props.car.kilometros)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Fuel size={16} />
              <span>{props.car.combustible}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Settings size={16} />
              <span>{props.car.transmision}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar size={16} />
              <span>{props.car.año}</span>
            </div>
            <div className="flex items-center space-x-1">
              <FaWhatsapp size={16} color="#25D366" />
              <a
                href={props.car.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#25D366]"
              >
                Propietario
              </a>
            </div>
            <div
              className="flex items-center space-x-1 cursor-pointer"
              onClick={handleCopyClick}
            >
              <IoMdCopy size={16} color="#0541c4" />
              <span className="w-32 break-words text-[#0541c4]">
                {props.car._id!.toString()}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <CgProfile size={16} />
              <span className="w-32 break-words">
                {props.car.clienteId?.nombre as keyof Cliente}
              </span>
            </div>
            <div
              className="flex items-center space-x-1 cursor-pointer"
              onClick={handleCopyClick}
            >
              <TfiEmail size={15} />
              <span className="w-32 break-words">
                {props.car.clienteId?.email || "correo@email.com"}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <MdOutlinePublish size={20} />
              <span className="w-32 break-words">
                Publicado: {daysSince(props.car.createdAt)} d
              </span>
            </div>
            {props.car.fechaDeArchivo && <div
              className="flex items-center space-x-1 cursor-pointer"
              onClick={handleCopyClick}
            >
              <BsArchive  size={15} />
              <span className="w-32 break-words">
                Archivado: {daysSince(props.car.fechaDeArchivo)} d
              </span>
            </div>}
          </div>
          <div className="flex items-center mt-5">
            <div className="">
              {props.car.avaluo && (
                <span className="line-through text-gray-400 text-sm">
                  {formatPrice(props.car.avaluo)}
                </span>
              )}

              <p className="text-2xl font-semibold text-gray-900 mb-4">
                {formatPrice(props.car.precio)}
              </p>
            </div>
            <button
              className="flex justify-end items-center gap-2 w-full text-gray-900 font-semibold text-[0.9rem] md:text-[1rem]  hover:underline cursor-pointer"
              onClick={toggleMenu}
            >
              <FaEllipsisVertical size={25} />
            </button>
          </div>
        </CardContent>
      </Card>
      {isMenuVisible && (
        <div
          ref={menuRef}
          className="absolute top-80 right-2 bg-white shadow-lg rounded-md w-40 z-10"
        >
          <ul className="list-none p-2">
            <li
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-4 justify-start items-center"
              onClick={() => {
                props.setCar(props.car)
                props.onOpenModal()
              }}
            >
              <FaRegEdit />
              <span>Edit</span>
            </li>
            <li
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-4 justify-start items-center"
              onClick={() => {
                if (typeof props.car._id === "string") {
                  onOpen()
                }
                setIsMenuVisible(false)
              }}
            >
              <BsTrash />
              <span>Delete</span>
            </li>
            <li
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex gap-4 justify-start items-center"
              onClick={() => {
                navitage(`/carros/${props.car._id}`)
                setIsMenuVisible(false)
              }}
            >
              <FaRegEye />
              <span>Detalles</span>
            </li>
          </ul>
        </div>
      )}
      <Modal isOpen={openModal} onClose={() => setOpenModal(false)}>
        <Modal.Body>
          <Modal.Header>Eliminar Carro</Modal.Header>
          <div className="w-full flex flex-col justify-center">
            <p className="sm:py-8 ">
              Estás segur@ que quieres eliminar el vehículo{" "}
            </p>
            <span className="font-bold mx-1">
              {props.car.marca} {props.car.modelo} {props.car.año} ?
            </span>
          </div>
          <Modal.Footer>
            <Modal.Action
              type="submit"
              styles="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-900 sm:ml-3 sm:w-auto"
              onClick={() => {
                if (typeof props.car._id === "string") {
                  deleteCarMutation({
                    _id: props.car._id,
                    folderId: props.car.folderId!,
                  })
                }
              }}
            >
              Eliminar
            </Modal.Action>
            <Modal.Dismiss styles="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0 sm:w-auto">
              Cancelar
            </Modal.Dismiss>
          </Modal.Footer>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export const Card = (props: { children: ReactNode; className: string }) => {
  return (
    <div className={`bg-white rounded-2xl shadow-md ${props.className}`}>
      {props.children}
    </div>
  )
}

export const CardContent = (props: {
  children: ReactNode
  className?: string
}) => {
  return <div className={`p-4 ${props.className}`}>{props.children}</div>
}

export default CarCard
